import {
  CONNECTOR_APPROVAL_LEVEL_STRICTNESS,
  TOOL_STANCE_STRICTNESS,
} from "../constants/connector-approval";
import {
  EActionRailBadge,
  EConnectorActionRail,
  EConnectorApprovalLevel,
  EToolApprovalStance,
  EToolSensitivity,
  type IConnectorActionInfo,
  type IFilterCatalogActionsByConnectedRailsProps,
  type IResolveConnectorApprovalLevelLabelProps,
  type IResolveEffectiveStanceProps,
} from "../types/connector-approval";
import { EConnectorType } from "../types/integration";

// NOTE: UI mirror of the server's stance resolver (level × sensitivity) used to
// show what an action will do when it "follows" the connector default. The
// server remains the source of truth at runtime — this only drives the hint.
// Shared verbatim by the web and mobile settings panels.
export const resolveEffectiveStance = ({
  level,
  sensitivity,
}: IResolveEffectiveStanceProps): EToolApprovalStance => {
  switch (level) {
    case EConnectorApprovalLevel.NEVER_ASK:
      return EToolApprovalStance.ALLOW;
    case EConnectorApprovalLevel.ALWAYS_ASK:
      return EToolApprovalStance.ASK;
    case EConnectorApprovalLevel.ASK_ON_CHANGES:
      return sensitivity === EToolSensitivity.READ
        ? EToolApprovalStance.ALLOW
        : EToolApprovalStance.ASK;
    case EConnectorApprovalLevel.ASK_ON_IMPORTANT:
      return sensitivity === EToolSensitivity.DESTRUCTIVE
        ? EToolApprovalStance.ASK
        : EToolApprovalStance.ALLOW;
    default:
      return EToolApprovalStance.ASK;
  }
};

const isLooserThanFloor = <T extends string>({
  value,
  floor,
  strictnessOrder,
}: {
  value: T;
  floor: T | undefined;
  strictnessOrder: readonly T[];
}): boolean => {
  if (!floor) {
    return false;
  }

  const valueRank = strictnessOrder.indexOf(value);
  const floorRank = strictnessOrder.indexOf(floor);

  return valueRank !== -1 && floorRank !== -1 && valueRank < floorRank;
};

// NOTE: True when `level` is strictly looser than the org-imposed `floor` — such
// options must be disabled in the per-user level picker, since the org floor
// clamps them up at runtime anyway. A missing floor (undefined) never disables.
export const isConnectorLevelLooserThanFloor = (
  level: EConnectorApprovalLevel,
  floor: EConnectorApprovalLevel | undefined,
): boolean =>
  isLooserThanFloor({
    value: level,
    floor,
    strictnessOrder: CONNECTOR_APPROVAL_LEVEL_STRICTNESS,
  });

// NOTE: True when a per-action `stance` is strictly looser than the `floor`
// stance the org enforces for that action's sensitivity.
export const isToolStanceLooserThanFloor = (
  stance: EToolApprovalStance,
  floor: EToolApprovalStance | undefined,
): boolean =>
  isLooserThanFloor({
    value: stance,
    floor,
    strictnessOrder: TOOL_STANCE_STRICTNESS,
  });

// NOTE: NSAI-4090 — renders an ORG-level minimum the way the org admin screen
// does. NEVER_ASK is not "Never ask" in this context: as a floor it means the
// organization imposes no minimum and each member decides for themselves, which
// is the wording OrgConnectorRow uses. Every other level reads as its own label.
// Shared so the web and mobile notification cards can't drift apart on it.
export const resolveConnectorApprovalLevelLabel = ({
  translate,
  level,
}: IResolveConnectorApprovalLevelLabelProps): string =>
  level === EConnectorApprovalLevel.NEVER_ASK
    ? translate("org.noMinimum")
    : translate(`level.${level}`);

// NOTE: NSAI-5206 — action descriptions from the catalog are written FOR THE
// MODEL: raw Markdown (`[See the docs](url)`, `**bold**`, `` `code` ``, bullets),
// a trailing "IMPORTANT: The arguments have specific formats…" arg-spec block,
// and often several paragraphs of usage guidance ("Do NOT use this…", recipes).
// Rendered literally in the settings list, that reads as a wall of Markdown.
// This strips the Markdown + the model-only tail and collapses whitespace into a
// single clean paragraph — the FULL human-readable text. Visual length is left
// to the consumer: web truncates it to one line via CSS and reveals the whole
// thing in a tooltip, mobile clamps it with numberOfLines. Shared so the two
// platforms clean the text identically.
export const formatConnectorActionDescription = (
  rawDescription: string,
): string => {
  if (!rawDescription) return "";

  return (
    rawDescription
      // NOTE: Drop the Pipedream "IMPORTANT: The arguments have specific
      // formats…" tail — it documents JSON arg shapes for the model, never
      // useful to a human.
      .replace(
        /\s*IMPORTANT:\s*The arguments have specific formats[\s\S]*$/i,
        "",
      )
      // Markdown images: ![alt](url) -> removed (drop the alt + the leading `!`,
      // which the link rule below would otherwise leave dangling)
      .replace(/!\[[^\]]*\]\([^)]*\)/g, "")
      // Markdown links: [label](url) -> label
      .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
      // Bold: **text** / __text__ -> text. Non-greedy so a nested single `*`
      // (e.g. "**Bold *italic* text**") doesn't block the whole span from
      // matching and leave literal asterisks behind.
      .replace(/\*\*(.+?)\*\*/g, "$1")
      .replace(/__(.+?)__/g, "$1")
      // Italic: *text* / _text_ -> text. Runs AFTER bold so the `**`/`__` pairs
      // are already gone. The leading `(^|[^\w*])` / `(^|[^\w_])` boundary (a
      // capture, not a lookbehind — Hermes lacks lookbehind) plus the closing
      // lookahead keep mid-word markers intact: "a*b*c" and "file_name" are not
      // emphasis and survive untouched.
      .replace(/(^|[^\w*])\*([^*\n]+?)\*(?=[^\w*]|$)/g, "$1$2")
      .replace(/(^|[^\w_])_([^_\n]+?)_(?=[^\w_]|$)/g, "$1$2")
      // Fenced code blocks: ```lang\ncode\n``` -> removed entirely (model-only
      // recipes). Must run BEFORE the inline-code rule, which is not fence-aware
      // and would otherwise mangle the fences into stray backticks + lang tag.
      .replace(/```[\s\S]*?```/g, "")
      // Inline code: `code` -> code
      .replace(/`([^`]+)`/g, "$1")
      // Leading heading (#) and blockquote (>) markers per line
      .replace(/^\s{0,3}#{1,6}\s+/gm, "")
      .replace(/^\s{0,3}>\s?/gm, "")
      // Leading bullet / numbered-list markers per line
      .replace(/^\s*[-*+]\s+/gm, "")
      .replace(/^\s*\d+\.\s+/gm, "")
      // Collapse all runs of whitespace/newlines into a single space
      .replace(/\s+/g, " ")
      .trim()
  );
};

// NOTE: NSAI-5688 — the settings list wants only a SHORT, user-facing summary of an
// action, not the full model-facing prose. formatConnectorActionDescription already
// strips Markdown + the arg-spec tail but keeps every sentence, so technical detail
// ("via Microsoft Graph", "Returns { count, folders } …", "@odata.count", parameter
// mechanics) still shows. This keeps only the FIRST sentence — the plain "what it
// does" line — which leaves the already-short native descriptions ("Gets user Profile
// in Gmail.") untouched while trimming the verbose ones. Shared so web and mobile
// summarise identically; the full cleaned text stays available for a tooltip.
// NOTE: NSAI-5688 — abbreviations whose trailing "." is NOT a sentence end. Without
// this, "…(UUID, e.g. 8566255a…)" summarises to the unbalanced fragment "…(UUID, e.g."
// — real catalog entries hit this. Compared lower-cased against the text preceding the
// period, so "E.g." matches too.
const NON_TERMINAL_ABBREVIATIONS = [
  "e.g",
  "i.e",
  "etc",
  "vs",
  "cf",
  "approx",
  "fig",
  "no",
  "al",
] as const;

interface IIsAbbreviationPeriodProps {
  text: string;
  periodIndex: number;
}

const isAbbreviationPeriod = ({
  text,
  periodIndex,
}: IIsAbbreviationPeriodProps): boolean => {
  // NOTE: Review — compare the WHOLE preceding word, never a suffix. A bare
  // `endsWith` matched any word merely ENDING in one of these ("con-fig.",
  // "hospit-al.", "casi-no."), so an ordinary sentence end was treated as an
  // abbreviation and the scan ran on into the next sentence — the same
  // truncation-class bug this summariser exists to fix, in the opposite
  // direction. `[a-z.]+$` grabs the trailing token (dots included, so "e.g"
  // survives intact) and it must equal an entry exactly.
  const precedingToken =
    text
      .slice(0, periodIndex)
      .toLowerCase()
      .match(/[a-z.]+$/)?.[0] ?? "";
  return NON_TERMINAL_ABBREVIATIONS.some(
    (abbreviation) => abbreviation === precedingToken,
  );
};

export const summarizeConnectorActionDescription = (
  rawDescription: string,
): string => {
  const cleaned = formatConnectorActionDescription(rawDescription);
  if (!cleaned) return "";

  // NOTE: Scan for the first REAL sentence end rather than regex-matching the first
  // terminator. A terminator counts only when it is followed by whitespace or the end
  // of the string — so a decimal ("1.5") and a URL host ("docs.microsoft.com") never
  // split it — and when the word before it is not a known abbreviation. Anything
  // without a real terminator returns the whole cleaned line, which errs toward
  // showing too much rather than truncating mid-sentence.
  for (let index = 0; index < cleaned.length; index += 1) {
    const character = cleaned[index];
    if (character !== "." && character !== "!" && character !== "?") {
      continue;
    }
    const next = cleaned[index + 1];
    if (next !== undefined && !/\s/.test(next)) {
      continue;
    }
    if (
      character === "." &&
      isAbbreviationPeriod({ text: cleaned, periodIndex: index })
    ) {
      continue;
    }
    return cleaned.slice(0, index + 1).trim();
  }

  return cleaned;
};

// NOTE: NSAI-5497/5498 — maps a connected integration's connectorType to the catalog
// action rail it provisions at runtime. COMPOSIO returns null: Composio tools are not
// represented in the action catalog (the settings panel is not shown for composio-only
// connections), so a composio row contributes no rail. Shared by web and mobile so both
// derive connected rails identically.
export const connectorTypeToActionRail = (
  connectorType: EConnectorType,
): EConnectorActionRail | null => {
  switch (connectorType) {
    case EConnectorType.INTERNAL:
      return EConnectorActionRail.NATIVE;
    case EConnectorType.PIPEDREAM:
      return EConnectorActionRail.PIPEDREAM;
    default:
      return null;
  }
};

// NOTE: NSAI-5497/5498 — keep only actions runnable on at least one connected rail, so
// the settings UI never advertises an action the user's connected rail can't fire (the
// core drift fix). Fails OPEN: with no known connected rails, returns every action rather
// than hiding the whole list on a data gap.
export const filterCatalogActionsByConnectedRails = ({
  actions,
  connectedRails,
}: IFilterCatalogActionsByConnectedRailsProps): IConnectorActionInfo[] => {
  if (connectedRails.length === 0) return actions;
  return actions.filter(
    (action) =>
      // NOTE: `rails` is guaranteed by the type, but the catalog crosses an HTTP boundary
      // with no runtime schema validation — a version-skew response (old cached payload
      // without `rails`) must degrade gracefully (keep the action) rather than throw in the
      // settings panel.
      !Array.isArray(action.rails) ||
      action.rails.some((rail) => connectedRails.includes(rail)),
  );
};

// NOTE: NSAI-5497/5498 — the per-action rail badge is only meaningful when BOTH rails are
// connected: with a single connected rail every visible action is on that rail, so the
// badge would be noise.
export const shouldShowActionRailBadge = (
  connectedRails: EConnectorActionRail[],
): boolean =>
  connectedRails.includes(EConnectorActionRail.NATIVE) &&
  connectedRails.includes(EConnectorActionRail.PIPEDREAM);

// NOTE: NSAI-5497/5498 — stable badge state for one action (Native / Pipedream / both),
// so web and mobile render identical labels. The caller maps the discriminant to i18n
// text and styling.
export const getActionRailBadge = (
  rails: EConnectorActionRail[],
): EActionRailBadge => {
  // NOTE: defensive against a version-skew catalog payload missing `rails` (see
  // filterCatalogActionsByConnectedRails) — default to NATIVE rather than throwing.
  const safeRails = Array.isArray(rails) ? rails : [];
  const hasNative = safeRails.includes(EConnectorActionRail.NATIVE);
  const hasPipedream = safeRails.includes(EConnectorActionRail.PIPEDREAM);
  if (hasNative && hasPipedream) return EActionRailBadge.BOTH;
  if (hasPipedream) return EActionRailBadge.PIPEDREAM;
  return EActionRailBadge.NATIVE;
};
